import { useMemo, useState } from "react";
import { ChevronDown, Clock3, FileText, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";

export type ProgressNodeStatus = "queued" | "running" | "done" | "failed";

export type ProgressNode = {
  id: string;
  title: string;
  desc: string;
  status: ProgressNodeStatus;
  costMs?: number;
  logs?: string[];
};

export function ProgressBoard({
  nodes,
  className,
}: {
  nodes: ProgressNode[];
  className?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  const summary = useMemo(() => {
    const total = nodes.length || 1;
    const done = nodes.filter((n) => n.status === "done").length;
    const running = nodes.some((n) => n.status === "running");
    const failed = nodes.some((n) => n.status === "failed");
    const pct = Math.round((done / total) * 100);
    return { pct, done, total, running, failed };
  }, [nodes]);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">评价进度看板</div>
          <div className="mt-1 text-xs text-muted-foreground">节点流转展示真实运行过程（演示）。</div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums",
              summary.failed
                ? "border-destructive/30 bg-destructive/10 text-destructive"
                : summary.running
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-border bg-muted/20 text-muted-foreground",
            )}
          >
            {summary.failed ? "失败" : summary.running ? "运行中" : "已完成"} · {summary.pct}%
          </span>
          <span className="rounded-full border border-border bg-muted/20 px-2.5 py-1 text-xs font-medium text-muted-foreground">
            {summary.done}/{summary.total}
          </span>
        </div>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${summary.pct}%` }} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {nodes.map((n) => {
          const isOpen = openId === n.id;
          const tone =
            n.status === "done"
              ? "border-border bg-card"
              : n.status === "running"
                ? "border-primary/30 bg-primary/5"
                : n.status === "failed"
                  ? "border-destructive/30 bg-destructive/5"
                  : "border-border bg-card";
          const dot =
            n.status === "done"
              ? "bg-primary/70"
              : n.status === "running"
                ? "bg-primary"
                : n.status === "failed"
                  ? "bg-destructive"
                  : "bg-muted-foreground/40";
          return (
            <div key={n.id} className={cn("rounded-2xl border p-4 shadow-sm", tone)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn("mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full", dot)} />
                    <div className="truncate text-sm font-semibold text-foreground">{n.title}</div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{n.desc}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpenId((cur) => (cur === n.id ? null : n.id))}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <ListChecks className="h-3.5 w-3.5" />
                  详情
                  <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
                </button>
              </div>

              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5" />
                {typeof n.costMs === "number" ? `${Math.max(1, Math.round(n.costMs / 100) / 10)}s` : "—"}
                <span className="text-muted-foreground/40">·</span>
                <FileText className="h-3.5 w-3.5" />
                {n.logs?.length ? `${n.logs.length} 条日志` : "无日志"}
              </div>

              {isOpen ? (
                <div className="mt-3 space-y-2 rounded-xl border border-border bg-background p-3">
                  {(n.logs?.length ? n.logs : ["（演示）该节点暂无日志。"]).map((l, idx) => (
                    <div key={idx} className="text-xs leading-relaxed text-muted-foreground">
                      {l}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

